var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { fetchConditions, fetchOverview } from '@alltrails/shared/utils/requests/weatherRequests';
import { adjustFromSameNominalTimeForUser, adjustToSameNominalTimeForUser, toZonedISOString } from '@alltrails/shared/utils/timeHelpers';
var initialState = {
    // We won't actually use this initial value of currentDateTime since it needs to be set relative to the time a new entity is added
    currentDateTime: '',
    alongTheTrail: undefined,
    alongTheTrailLoadState: 'Uninitialized',
    conditions: undefined,
    lifestyleLoadState: 'Uninitialized',
    mosquitoActivity: undefined,
    overviewLoadState: 'Uninitialized',
    overviewWeather: undefined
};
var weatherAdapter = createEntityAdapter({
    selectId: function (weatherInfo) { return weatherInfo.mapId; }
});
export var getWeatherInfo = weatherAdapter.getSelectors(function (state) { return state.weather; }).selectById;
export var getConditions = createAsyncThunk('weather/getConditions', function (_a, _b) {
    var mapId = _a.mapId, startDateTime = _a.startDateTime;
    var getState = _b.getState, rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var state, displayMetric, weatherInfo;
        return __generator(this, function (_c) {
            state = getState();
            displayMetric = state.context.displayMetric;
            weatherInfo = getWeatherInfo(state, mapId);
            if (!weatherInfo) {
                return [2 /*return*/, rejectWithValue('Weather info does not exist for the provided map')];
            }
            return [2 /*return*/, fetchConditions(mapId, startDateTime || (weatherInfo === null || weatherInfo === void 0 ? void 0 : weatherInfo.currentDateTime), displayMetric ? 'm' : 'i')];
        });
    });
});
export var getOverview = createAsyncThunk('weather/getOverview', function (_a) {
    var mapId = _a.mapId;
    return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_b) {
        return [2 /*return*/, fetchOverview(mapId)];
    }); });
});
/**
 * Aggregate call to weather service's overview & conditions endpoint by dispatching the respective actions.
 * Typically, this can/should be used to initialize weather information for a given mapId.
 */
export var getMapWeather = createAsyncThunk('weather/getMapWeather', function (_a, _b) {
    var mapId = _a.mapId;
    var getState = _b.getState, dispatch = _b.dispatch;
    return __awaiter(void 0, void 0, void 0, function () {
        var proUser, overviewPromise, conditionsPromise;
        return __generator(this, function (_c) {
            proUser = getState().context.proUser;
            dispatch(weatherSlice.actions.addMapWeather(mapId));
            if (proUser) {
                overviewPromise = dispatch(getOverview({ mapId: mapId }));
                conditionsPromise = dispatch(getConditions({ mapId: mapId }));
                return [2 /*return*/, Promise.allSettled([overviewPromise, conditionsPromise])];
            }
            return [2 /*return*/, dispatch(getOverview({ mapId: mapId }))];
        });
    });
});
/**
 * Sets the provided date & fetches new condition information that aligns with the new date.
 *
 * NOTE: date is stripped of the timezone and treated as if it were a UTC date. For any timezone requirements, consumers should use the `secondsFromUTC` field.
 */
export var updateDate = createAsyncThunk('weather/updateDate', function (_a, _b) {
    var mapId = _a.mapId, newDate = _a.newDate;
    var dispatch = _b.dispatch, getState = _b.getState, rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var state, adjustedNominalDateTime, secondsFromUtc, currentAdjustedDate, localDate, today, localToday, adjustedDate, zonedDate, dateTime, proUser;
        return __generator(this, function (_c) {
            state = getWeatherInfo(getState(), mapId);
            if (!state) {
                return [2 /*return*/, rejectWithValue('Weather info does not exist for the provided map')];
            }
            adjustedNominalDateTime = state.adjustedNominalDateTime, secondsFromUtc = state.secondsFromUtc;
            currentAdjustedDate = new Date(adjustedNominalDateTime);
            localDate = new Date(newDate);
            localDate.setHours(currentAdjustedDate.getHours(), currentAdjustedDate.getMinutes(), currentAdjustedDate.getSeconds(), currentAdjustedDate.getMilliseconds());
            today = new Date();
            localToday = adjustToSameNominalTimeForUser(today.toISOString(), secondsFromUtc * 1000);
            if (localDate.getFullYear() === localToday.getFullYear() &&
                localDate.getMonth() === localToday.getMonth() &&
                localDate.getDate() === localToday.getDate() &&
                localDate.getHours() <= localToday.getHours()) {
                // if the current time is between 11 and midnight, do not adjust the time to the next hour to avoid showing the next day's conditions
                localDate.setHours(localToday.getHours() + (localToday.getHours() === 23 ? 0 : 1), 0, 0);
            }
            adjustedDate = adjustFromSameNominalTimeForUser(localDate, secondsFromUtc * 1000);
            zonedDate = toZonedISOString(adjustedDate, secondsFromUtc * 1000);
            dateTime = dispatch(weatherSlice.actions.setDateTime({
                id: mapId,
                changes: {
                    currentDateTime: zonedDate,
                    adjustedNominalDateTime: adjustToSameNominalTimeForUser(zonedDate, secondsFromUtc * 1000).toISOString()
                }
            }));
            proUser = getState().context.proUser;
            if (proUser) {
                return [2 /*return*/, dispatch(getConditions({ mapId: mapId, startDateTime: zonedDate }))];
            }
            return [2 /*return*/, dateTime];
        });
    });
});
/**
 * Sets the provided time & fetches new condition information that aligns with the new time. Only the time field is used in the provided `Date` object
 * and is appended with the date currently set in the state.
 *
 * NOTE: time is stripped of the timezone and treated as if it were a UTC time. For any timezone requirements, consumers should use the `secondsFromUtc` field.
 */
export var updateTime = createAsyncThunk('weather/updateTime', function (_a, _b) {
    var mapId = _a.mapId, newTime = _a.newTime;
    var dispatch = _b.dispatch, getState = _b.getState, rejectWithValue = _b.rejectWithValue;
    return __awaiter(void 0, void 0, void 0, function () {
        var state, adjustedNominalDateTime, secondsFromUtc, newDate, currentAdjustedDate, adjustedDate, zonedDate;
        return __generator(this, function (_c) {
            state = getWeatherInfo(getState(), mapId);
            if (!state || !state.currentDateTime) {
                return [2 /*return*/, rejectWithValue('Weather info does not exist for the provided map')];
            }
            adjustedNominalDateTime = state.adjustedNominalDateTime, secondsFromUtc = state.secondsFromUtc;
            newDate = new Date(newTime);
            currentAdjustedDate = new Date(adjustedNominalDateTime);
            newDate.setFullYear(currentAdjustedDate.getFullYear());
            newDate.setMonth(currentAdjustedDate.getMonth());
            newDate.setDate(currentAdjustedDate.getDate());
            adjustedDate = adjustFromSameNominalTimeForUser(newDate, secondsFromUtc * 1000);
            zonedDate = toZonedISOString(adjustedDate, secondsFromUtc * 1000);
            dispatch(weatherSlice.actions.setDateTime({
                id: mapId,
                changes: {
                    currentDateTime: zonedDate,
                    adjustedNominalDateTime: adjustToSameNominalTimeForUser(zonedDate, secondsFromUtc * 1000).toISOString()
                }
            }));
            return [2 /*return*/, dispatch(getConditions({ mapId: mapId, startDateTime: zonedDate }))];
        });
    });
});
export var weatherSlice = createSlice({
    name: 'weather',
    initialState: weatherAdapter.getInitialState(),
    reducers: {
        addMapWeather: function (state, action) {
            if (action.payload) {
                weatherAdapter.upsertOne(state, __assign(__assign({}, initialState), { mapId: action.payload }));
            }
        },
        setDateTime: weatherAdapter.updateOne
    },
    extraReducers: function (builder) {
        builder.addCase(getConditions.pending, function (state, _a) {
            var meta = _a.meta;
            var mapId = meta.arg.mapId;
            weatherAdapter.updateOne(state, {
                id: mapId,
                changes: { alongTheTrailLoadState: 'Loading', lifestyleLoadState: 'Loading' }
            });
        });
        builder.addCase(getConditions.fulfilled, function (state, _a) {
            var payload = _a.payload, meta = _a.meta;
            var mapId = meta.arg.mapId;
            var _b = payload.lifestyleConditions, mosquitoActivity = _b.mosquitoActivity, trailCondition = _b.trailCondition, trailConditionSummaryText = _b.trailConditionSummaryText, _c = payload.weatherAlongTheTrail, polylineData = _c.polylineData, coldestPoint = _c.coldestPoint, hottestPoint = _c.hottestPoint, startingAt = _c.startingAt, secondsFromUtc = _c.secondsFromUtc;
            weatherAdapter.updateOne(state, {
                id: mapId,
                changes: {
                    alongTheTrail: { polylineData: polylineData, coldestPoint: coldestPoint, hottestPoint: hottestPoint },
                    conditions: {
                        conditions: trailCondition,
                        summaryText: trailConditionSummaryText
                    },
                    currentDateTime: startingAt,
                    adjustedNominalDateTime: adjustToSameNominalTimeForUser(startingAt, secondsFromUtc * 1000).toISOString(),
                    mosquitoActivity: mosquitoActivity,
                    secondsFromUtc: secondsFromUtc,
                    alongTheTrailLoadState: 'Success',
                    lifestyleLoadState: 'Success'
                }
            });
        });
        builder.addCase(getConditions.rejected, function (state, _a) {
            var meta = _a.meta;
            var mapId = meta.arg.mapId;
            weatherAdapter.updateOne(state, {
                id: mapId,
                changes: { alongTheTrailLoadState: 'Error', lifestyleLoadState: 'Error' }
            });
        });
        builder.addCase(getOverview.pending, function (state, _a) {
            var meta = _a.meta;
            var mapId = meta.arg.mapId;
            weatherAdapter.updateOne(state, {
                id: mapId,
                changes: { overviewLoadState: 'Loading' }
            });
        });
        builder.addCase(getOverview.fulfilled, function (state, _a) {
            var payload = _a.payload, meta = _a.meta;
            var mapId = meta.arg.mapId;
            var dailyWeather = payload.dailyWeather;
            weatherAdapter.updateOne(state, {
                id: mapId,
                changes: {
                    overviewLoadState: 'Success',
                    overviewWeather: payload.dailyWeather.slice(0, 7),
                    adjustedNominalDateTime: adjustToSameNominalTimeForUser(dailyWeather[0].dateTime, dailyWeather[0].secondsFromUtc * 1000).toISOString(),
                    secondsFromUtc: dailyWeather[0].secondsFromUtc,
                    currentDateTime: dailyWeather[0].dateTime
                }
            });
        });
        builder.addCase(getOverview.rejected, function (state, _a) {
            var meta = _a.meta;
            var mapId = meta.arg.mapId;
            weatherAdapter.updateOne(state, {
                id: mapId,
                changes: { overviewLoadState: 'Error' }
            });
        });
    }
});
export var addMapWeather = weatherSlice.actions.addMapWeather;
export default weatherSlice.reducer;
//# sourceMappingURL=index.js.map